<template>
  <v-dialog
    v-model="isOpen"
    max-width="800"
    overlay-opacity="0.8"
    transition="scale-transition"
    @keydown.esc="close"
  >
    <v-card class="spotlight-search">
      <!-- Champ de recherche -->
      <div class="search-header">
        <v-text-field
          ref="searchInput"
          v-model="searchQuery"
          placeholder="Rechercher un client..."
          solo
          flat
          hide-details
          prepend-inner-icon="search"
          class="spotlight-input"
          @input="onSearchInput"
          @keydown="onKeyDown"
        >
          <template v-slot:append>
            <v-btn
              icon
              @click="close"
              class="close-spotlight-btn"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>

      <!-- Résultats -->
      <div class="search-results" v-if="searchQuery || displayResults.length > 0">
        <!-- Clients trouvés ou liste pré-chargée -->
        <div v-if="displayResults.length > 0" class="results-container">
          <!-- Sur téléphone, les lignes sont plus hautes : ID et serial passent sous le nom sur deux lignes -->
          <v-virtual-scroll
            :items="displayResults"
            :item-height="isMobile ? MOBILE_ROW_HEIGHT : ROW_HEIGHT"
            :height="isMobile ? 'calc(100vh - 72px)' : 400"
          >
            <template v-slot:default="{ item, index }">
              <!-- En-tête de catégorie -->
              <div
                v-if="item.isHeader"
                class="category-header-spotlight"
                :key="`header-${index}`"
              >
                <div class="category-title-spotlight">
                  <v-icon left small color="primary">{{ getCategoryIcon(item.type) }}</v-icon>
                  {{ item.type }} ({{ item.count }})
                </div>
              </div>

              <!-- Élément client -->
              <v-list-item
                v-else
                :key="`customer-${item.clientId}-${index}`"
                @click="selectCustomer(item)"
                :class="{ 'highlighted-spotlight': index === highlightedIndex }"
                class="customer-item-spotlight"
              >
                <v-list-item-avatar size="36">
                  <img :src="getClientLogo(item)" :alt="item.name" class="customer-logo-spotlight" />
                </v-list-item-avatar>
                
                <v-list-item-content>
                  <v-list-item-title class="customer-name-spotlight">
                    <span v-html="highlightMatch(item.name)"></span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="customer-subtitle-spotlight">
                    <span v-html="highlightMatch('ID: ' + item.clientId)"></span>
                    <span v-if="item.serial" class="ml-2">
                      • <span v-html="highlightMatch('Série: ' + item.serial)"></span>
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            x-small
                            class="ml-1 copy-serial-spotlight-btn"
                            @click.stop="copyToClipboard(item.serial, 'Numéro de série')"
                          >
                            <v-icon x-small color="primary">content_copy</v-icon>
                          </v-btn>
                        </template>
                        <span>Copier le numéro de série</span>
                      </v-tooltip>
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <!-- Le type est déjà dans l'en-tête de catégorie : la puce laisse la place au serial -->
                <v-list-item-action v-if="!isMobile">
                  <v-chip small outlined color="primary">
                    {{ item.type }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-virtual-scroll>
        </div>

        <!-- Aucun résultat -->
        <div v-else-if="searchQuery" class="no-results-spotlight">
          <v-icon size="40" color="var(--ink-faint)">search_off</v-icon>
          <p class="mt-4 mb-0">Aucun client trouvé pour "{{ searchQuery }}"</p>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import groupby from 'lodash.groupby';
  import { mapState } from 'vuex';
  import MobileMixin from '@/mixins/MobileMixin';
  import CustomerService from '../services/CustomerService';

  // Hauteurs de ligne du défilement virtuel, tenues en miroir des règles de style ci-dessous
  const ROW_HEIGHT = 56;
  const MOBILE_ROW_HEIGHT = 68;

  export default {
    name: 'SpotlightSearch',
    mixins: [MobileMixin],
    data() {
      return {
        ROW_HEIGHT,
        MOBILE_ROW_HEIGHT,
        searchQuery: '',
        highlightedIndex: -1,
        searchTimeout: null,
      };
    },
    computed: {
      isOpen: {
        get() {
          return this.$store.state.spotlightSearch.isOpen;
        },
        set(value) {
          this.$store.dispatch('setSpotlightSearchOpen', value);
        }
      },

      ...mapState({
        customers: (state) => state.customers.list,
        selectedCustomer: (state) => state.customers.selected,
        notionClients: (state) => state.customers.notionClients,
      }),

      // Clients filtrés avec recherche intelligente
      filteredCustomers() {
        if (!this.searchQuery || this.searchQuery.length < 1) {
          return [];
        }

        const normalizedQuery = this.normalizeString(this.searchQuery);
        const matchingCustomers = this.customers.filter(customer => {
          const normalizedName = this.normalizeString(customer.name);
          const normalizedId = this.normalizeString(customer.clientId?.toString() || '');
          const normalizedSerial = this.normalizeString(customer.serial?.toString() || '');
        
          return normalizedName.includes(normalizedQuery) || 
            normalizedId.includes(normalizedQuery) ||
            normalizedSerial.includes(normalizedQuery);
        });

        return this.organizeCustomersByType(matchingCustomers);
      },

      // Liste pré-chargée des clients (quand pas de recherche)
      preloadedCustomers() {
        if (!this.customers || this.customers.length === 0) return [];
      
        // Trier par type (Client en premier) puis par nom alphabétique
        const sortedCustomers = [...this.customers].sort((a, b) => {
          // Gérer les valeurs null/undefined
          const aType = a.type || '';
          const bType = b.type || '';
          const aName = a.name || '';
          const bName = b.name || '';
        
          // Prioriser les clients
          if (aType !== bType) {
            if (aType === 'Client') return -1;
            if (bType === 'Client') return 1;
            return aType.localeCompare(bType);
          }
          // Puis tri alphabétique par nom
          return aName.localeCompare(bName, 'fr', { sensitivity: 'base' });
        });

        return this.organizeCustomersByType(sortedCustomers);
      },

      // Résultats à afficher : recherche ou liste pré-chargée
      displayResults() {
        return this.searchQuery ? this.filteredCustomers : this.preloadedCustomers;
      },
    },

    watch: {
      isOpen(newValue) {
        if (newValue) {
          // Réinitialiser l'état
          this.searchQuery = '';
          this.highlightedIndex = -1;
        
          // Focus multiple avec délais échelonnés pour s'assurer que ça marche
          this.$nextTick(() => {
          this.$refs.searchInput?.focus();
          
          // Premier délai court
          setTimeout(() => {
            if (this.$refs.searchInput) {
              this.$refs.searchInput.focus();
            }
          }, 50);
          
          // Deuxième délai plus long pour les cas difficiles
          setTimeout(() => {
            if (this.$refs.searchInput && this.isOpen) {
              this.$refs.searchInput.focus();
              // S'assurer aussi que l'input HTML natif a le focus
              const inputElement = this.$refs.searchInput.$el.querySelector('input');
              if (inputElement) {
                inputElement.focus();
              }
            }
          }, 200);
          });
        }
      },

      // Auto-sélection si un seul résultat (seulement en mode recherche)
      filteredCustomers: {
        handler(newResults) {
          this.highlightedIndex = -1;
        
          // Auto-sélection seulement quand on tape une recherche
          if (!this.searchQuery) return;
        
          // Compter uniquement les clients (pas les en-têtes)
          const clientResults = newResults.filter(item => !item.isHeader);
        
          if (this.searchQuery && clientResults.length === 1) {
            clearTimeout(this.searchTimeout);
            // Délai réduit pour une meilleure réactivité
            this.searchTimeout = setTimeout(() => {
              const remainingClients = this.filteredCustomers.filter(item => !item.isHeader);
              if (remainingClients.length === 1 && this.searchQuery) {
                this.selectCustomer(remainingClients[0]);
              }
            }, 500); // Réduit de 1000ms à 500ms
          } else {
            // Si plus d'un résultat, annuler l'auto-sélection
            clearTimeout(this.searchTimeout);
          }
        },
        immediate: false
      }
    },

    methods: {
      // Normalisation de chaîne pour recherche insensible aux accents
      normalizeString(str) {
        if (!str) return '';
        return str
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
      },

      // Organisation des clients par type avec en-têtes
      organizeCustomersByType(customers) {
        if (!customers || customers.length === 0) return [];

        const grouped = groupby(customers, 'type');
        const result = [];

        const sortedTypes = Object.keys(grouped)
          .filter(type => type && type !== 'null')
          .sort((a, b) => {
            if (a === 'Client') return -1;
            if (b === 'Client') return 1;
            return a.localeCompare(b);
          });

        sortedTypes.forEach(type => {
          const typeCustomers = grouped[type].sort((a, b) => a.name.localeCompare(b.name));
        
          if (typeCustomers.length > 0) {
            result.push({
              isHeader: true,
              type: type,
              count: typeCustomers.length
            });
          
            result.push(...typeCustomers);
          }
        });

        return result;
      },

      onSearchInput() {
        // Annuler l'auto-sélection si l'utilisateur continue de taper
        clearTimeout(this.searchTimeout);
      },

      // Navigation au clavier
      onKeyDown(event) {
        const visibleItems = this.filteredCustomers.filter(item => !item.isHeader);
      
        // Annuler l'auto-sélection si l'utilisateur navigue
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
          clearTimeout(this.searchTimeout);
        }
      
        switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.highlightedIndex = Math.min(this.highlightedIndex + 1, visibleItems.length - 1);
          break;
        
        case 'ArrowUp':
          event.preventDefault();
          this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
          break;
        
        case 'Enter':
          event.preventDefault();
          // Annuler l'auto-sélection si l'utilisateur appuie sur Entrée
          clearTimeout(this.searchTimeout);
          if (this.highlightedIndex >= 0 && visibleItems[this.highlightedIndex]) {
            this.selectCustomer(visibleItems[this.highlightedIndex]);
          } else if (visibleItems.length === 1) {
            // Si un seul résultat et pas de sélection, prendre le seul résultat
            this.selectCustomer(visibleItems[0]);
          }
          break;
        
        case 'Escape':
          this.close();
          break;
        }
      },

      // Sélection d'un client
      selectCustomer(customer) {
        if (!customer || customer.isHeader) return;
      
        this.$store.dispatch('setSelectedCustomer', customer);

        this.$store.dispatch('addNotification', {
          text: `Client "${customer.name}" sélectionné`,
          color: 'success',
        });

        this.close();
      },

      close() {
        // Nettoyer le timeout d'auto-sélection
        clearTimeout(this.searchTimeout);
        this.isOpen = false;
        this.searchQuery = '';
        this.highlightedIndex = -1;
      },

      // Mise en surbrillance des correspondances
      highlightMatch(text) {
        if (!this.searchQuery) return text;
      
        const normalizedQuery = this.normalizeString(this.searchQuery);
        const normalizedText = this.normalizeString(text);
      
        const index = normalizedText.indexOf(normalizedQuery);
        if (index === -1) return text;
      
        const start = index;
        const end = index + this.searchQuery.length;
      
        return text.substring(0, start) + 
          '<strong class="highlight-spotlight">' + 
          text.substring(start, end) + 
          '</strong>' + 
          text.substring(end);
      },

      // Icônes des catégories
      getCategoryIcon(type) {
        const icons = {
          'Client': 'person',
          'Prospect': 'person_outline',
          'Partenaire': 'business',
          'Fournisseur': 'local_shipping'
        };
        return icons[type] || 'folder';
      },

      logoEnseigne(idEnseigne) {
        return CustomerService.getLogo(idEnseigne);
      },

      getClientLogo(customer) {
        // Trouver le client Notion correspondant
        const notionClient = this.notionClients?.find(client => client.serial === customer.serial);
      
        // Prioriser le logo Notion s'il est disponible
        if (notionClient && notionClient.notion && notionClient.notion.icone) {
          return notionClient.notion.icone;
        }
        // Sinon utiliser le logo de l'enseigne
        if (customer.idenseigne) {
          return this.logoEnseigne(customer.idenseigne);
        }
        // Fallback vers une icône par défaut
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjMzc2M0I0Ii8+Cjwvc3ZnPgo=';
      },

      // Copier dans le presse-papier
      copyToClipboard(element, value) {
        navigator.clipboard
          .writeText(element)
          .then(() => {
            this.$store.dispatch('addNotification', {
              text: `${value} copié dans votre presse-papier`,
              color: 'success',
            });
          })
          .catch((e) => {
            console.error(e);
            this.$store.dispatch('addNotification', {
              text: `Impossible de copier ${value} dans votre presse-papier`,
              color: 'error',
            });
          });
      },

      // Gestionnaire global des raccourcis clavier
      handleGlobalKeydown(event) {
        // ⌘K (Mac) ou Ctrl+K (PC) pour ouvrir
        if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
          event.preventDefault();
          this.$store.dispatch('openSpotlightSearch');
        
          // S'assurer du focus après ouverture via raccourci
          this.$nextTick(() => {
            setTimeout(() => {
              this.ensureFocus();
            }, 100);
          });
        }
        // Escape pour fermer
        else if (event.key === 'Escape' && this.isOpen) {
          this.close();
        }
      },

      // Méthode pour forcer le focus sur le champ de recherche
      ensureFocus() {
        if (this.isOpen && this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        
          // Focus aussi sur l'élément HTML natif
          const inputElement = this.$refs.searchInput.$el.querySelector('input');
          if (inputElement) {
            inputElement.focus();
            inputElement.select(); // Sélectionner le texte existant s'il y en a
          }
        }
      },
    },

    mounted() {
      // Ajouter l'écoute globale des raccourcis clavier
      document.addEventListener('keydown', this.handleGlobalKeydown);
    },

    beforeDestroy() {
      // Nettoyer les event listeners
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      document.removeEventListener('keydown', this.handleGlobalKeydown);
    }
  };
</script>

<style lang="scss" scoped>
// Palette de recherche : une carte du design system, sans dégradé ni flou
.spotlight-search {
  border-radius: var(--radius-lg) !important;
  overflow: hidden;
  background: var(--surface-raised);
  box-shadow: var(--shadow-md);
}

.search-header {
  border-bottom: 1px solid var(--line);

  .spotlight-input .v-input__slot {
    background: transparent !important;
    box-shadow: none !important;
    padding: var(--space-2) var(--space-4);

    input {
      font-size: 16px;
      color: var(--ink);

      &::placeholder {
        color: var(--ink-faint);
      }
    }

    .v-icon {
      color: var(--ink-muted);
    }
  }

  .close-spotlight-btn .v-icon {
    color: var(--ink-muted);
  }
}

.search-results {
  max-height: 500px;

  .results-container {
    background: var(--surface-raised);
  }
}

// En-tête de catégorie : même hauteur que les lignes (item-height du v-virtual-scroll)
.category-header-spotlight {
  background: var(--surface-sunken);
  padding: 0 var(--space-4) !important;
  border-bottom: 1px solid var(--line);
  height: 56px;
  display: flex !important;
  align-items: center !important;
  margin: 0 !important;

  .category-title-spotlight {
    font: var(--text-label);
    color: var(--ink-muted);
    display: flex;
    align-items: center;
  }
}

.customer-item-spotlight {
  cursor: pointer;
  margin: 0 !important;
  border-bottom: 1px solid var(--line);
  display: flex !important;
  align-items: center !important;

  &.v-list-item {
    padding: 0 var(--space-4) !important;
    height: 56px !important;
    min-height: 56px !important;
    max-height: 56px !important;
  }

  .v-list-item__content {
    padding: 0 !important;
    overflow: hidden;
  }

  .v-list-item__avatar {
    margin: 0 var(--space-3) 0 0 !important;
    align-self: center !important;
  }

  &:hover,
  &.highlighted-spotlight {
    background: var(--brand-soft);
  }

  .customer-logo-spotlight {
    width: 32px !important;
    height: 32px !important;
    object-fit: contain;
    object-position: center;
    border-radius: var(--radius-sm);
    display: block;
    background: var(--surface-raised);
    border: 1px solid var(--line);
    padding: 2px;
    flex-shrink: 0;
  }

  .customer-name-spotlight {
    font-size: 15px;
    font-weight: 500;
    color: var(--ink);
    line-height: 20px !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .customer-subtitle-spotlight {
    font: var(--text-small);
    color: var(--ink-muted) !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Bouton de copie du numéro de série
  .copy-serial-spotlight-btn {
    vertical-align: middle !important;
    margin-left: 4px !important;
    height: 20px !important;
    width: 20px !important;

    .v-icon {
      font-size: 14px !important;
    }
  }

  .v-list-item__action {
    margin: 0 !important;
    align-self: center !important;

    .v-chip {
      height: 22px !important;
      font-size: 12px !important;
    }
  }
}

// Surlignage du terme cherché (injecté en v-html, donc hors portée du scoped)
::v-deep .highlight-spotlight {
  background: var(--warning-soft);
  color: var(--ink);
  border-radius: 3px;
  padding: 0 2px;
  font-weight: 600;
}

.no-results-spotlight {
  text-align: center;
  padding: var(--space-7) var(--space-4);
  color: var(--ink-muted);

  p {
    font-size: 15px;
  }
}

// Téléphone : le dialogue est plein écran, la liste prend toute la hauteur restante ; chaque ligne
// fait 68 px (MOBILE_ROW_HEIGHT) pour écrire l'ID et le serial sous le nom, et le bouton de copie
// du serial se touche au pouce
@media (max-width: 599.98px) {
  .search-results {
    max-height: none;
  }

  .category-header-spotlight {
    height: 68px;
  }

  .customer-item-spotlight {
    &.v-list-item {
      height: 68px !important;
      min-height: 68px !important;
      max-height: 68px !important;
    }

    .customer-subtitle-spotlight {
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      line-height: 18px;
    }

    .copy-serial-spotlight-btn {
      height: 28px !important;
      width: 28px !important;

      .v-icon {
        font-size: 18px !important;
      }
    }
  }
}
</style>
